import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation } from 'react-router-dom';
import { BarChart3, ChevronDown, Ellipsis, KeyRound, LogOut } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { roleOf } from '../utils/roles';
import { GUIDE_ITEM, MORE_LABEL, NavItem, moreNavForRole, primaryNavForRole } from '../utils/navigation';

interface Props {
  onNavigate?: () => void;
  /** The narrow icon rail: labels are hidden and each icon names its page on hover or focus. */
  collapsed?: boolean;
}

// Whether a person left "More pages" open is remembered in this browser, per login.
const moreKey = (userId: unknown) => `jarvis.menu.more.${String(userId ?? 'anon')}`;
function readMoreOpen(userId: unknown): boolean {
  try {
    return window.localStorage.getItem(moreKey(userId)) === '1';
  } catch {
    return false;
  }
}
function writeMoreOpen(userId: unknown, open: boolean) {
  try {
    window.localStorage.setItem(moreKey(userId), open ? '1' : '0');
  } catch {
    /* a private window: the choice just is not remembered */
  }
}

interface RailTip {
  label: string;
  purpose?: string;
  top: number;
}

// The rail is 4.25rem wide (AppLayout). Header, avatar and items all sit 1rem to 0.75rem from the edge,
// so an icon is in the same place whether the menu is a rail or open.
export default function Sidebar({ onNavigate, collapsed = false }: Props) {
  const { user, logout } = useAuth();
  const role = roleOf(user);
  const primary = primaryNavForRole(role);
  const more = moreNavForRole(role);
  const { pathname } = useLocation();
  const onMorePage = more.some((g) => g.items.some((i) => pathname === i.path || pathname.startsWith(i.path + '/')));
  const [moreOpen, setMoreOpen] = useState(() => readMoreOpen(user?.id));
  const [tip, setTip] = useState<RailTip | null>(null);

  // Arriving on a page under "More pages" opens it, so the current page is always marked in the menu.
  useEffect(() => {
    if (onMorePage) setMoreOpen(true);
  }, [onMorePage, pathname]);

  const toggleMore = () => {
    const next = !moreOpen;
    setMoreOpen(next);
    writeMoreOpen(user?.id, next);
    setTip(null);
  };

  useEffect(() => {
    if (!collapsed) setTip(null);
  }, [collapsed]);

  // In the rail an icon has no words beside it, so hovering or tabbing to it names the page.
  const railTip = (label: string, purpose?: string) =>
    collapsed
      ? {
          onMouseEnter: (e: React.MouseEvent<HTMLElement>) => {
            const r = e.currentTarget.getBoundingClientRect();
            setTip({ label, purpose, top: r.top + r.height / 2 });
          },
          onFocus: (e: React.FocusEvent<HTMLElement>) => {
            let keyboard = false;
            try {
              keyboard = e.currentTarget.matches(':focus-visible');
            } catch {
              keyboard = false;
            }
            if (!keyboard) return;
            const r = e.currentTarget.getBoundingClientRect();
            setTip({ label, purpose, top: r.top + r.height / 2 });
          },
          onMouseLeave: () => setTip(null),
          onBlur: () => setTip(null),
        }
      : {};

  const initial = user?.displayName?.charAt(0) || user?.username?.charAt(0) || 'A';

  // One page in the menu; the Guide at the foot of it is the same kind of link.
  const menuLink = (item: NavItem) => {
    const Icon = item.icon;
    return (
      <NavLink
        key={item.path}
        to={item.path}
        end
        title={collapsed ? undefined : item.purpose}
        onClick={() => {
          setTip(null);
          onNavigate?.();
        }}
        {...railTip(item.label, item.purpose)}
        className={({ isActive }) =>
          `group relative w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm whitespace-nowrap transition-all duration-150 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
            isActive
              ? 'bg-primary-50 text-primary-700 font-semibold'
              : 'text-secondary-600 font-medium hover:bg-secondary-50 hover:text-secondary-900'
          }`
        }
      >
        {({ isActive }) => (
          <>
            {isActive && (
              <span className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-1 rounded-r-full bg-primary-600" />
            )}
            <Icon
              className={`w-5 h-5 shrink-0 transition-colors ${
                isActive ? 'text-primary-600' : 'text-secondary-400 group-hover:text-secondary-600'
              }`}
            />
            <span className={collapsed ? 'sr-only' : ''}>{item.label}</span>
          </>
        )}
      </NavLink>
    );
  };

  return (
    <div className="flex flex-col h-full bg-white border-r border-secondary-100">
      {/* Brand header */}
      <div className="flex items-center h-16 px-4 bg-gradient-to-r from-primary-600 to-primary-700 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 shrink-0 bg-white/95 rounded-xl shadow-sm flex items-center justify-center">
            <BarChart3 className="w-5 h-5 text-primary-600" />
          </div>
          {!collapsed && <h2 className="text-lg font-bold text-white tracking-tight whitespace-nowrap">Jira Worklog</h2>}
        </div>
      </div>

      {/* User profile */}
      <div className="px-4 py-4 border-b border-secondary-100" title={collapsed ? [user?.displayName || user?.username, user?.email].filter(Boolean).join('\n') : undefined}>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 ring-2 ring-primary-100 flex items-center justify-center shrink-0">
            <span className="text-white font-semibold">{initial}</span>
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-secondary-900 truncate">{user?.displayName || user?.username}</p>
              <p className="text-xs text-secondary-500 truncate">{user?.email}</p>
            </div>
          )}
        </div>
      </div>

      {/* Navigation: the few pages a role starts with, then "More pages" (utils/navigation.ts) */}
      <nav aria-label="Main" className="flex-1 px-3 py-4 overflow-y-auto overflow-x-hidden">
        <div className="space-y-1">{primary.map(menuLink)}</div>

        {more.length > 0 && (
          <div className={collapsed ? 'mt-3 border-t border-secondary-100 pt-3' : 'mt-3'}>
            <button
              type="button"
              onClick={toggleMore}
              aria-expanded={moreOpen}
              aria-controls="nav-more"
              title={collapsed ? undefined : 'The other pages you can open'}
              {...railTip(MORE_LABEL, 'The other pages you can open.')}
              className="group w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap text-secondary-600 hover:bg-secondary-50 hover:text-secondary-900 transition-colors motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
            >
              <Ellipsis className="w-5 h-5 shrink-0 text-secondary-500 group-hover:text-secondary-600 transition-colors" aria-hidden />
              <span className={collapsed ? 'sr-only' : ''}>{MORE_LABEL}</span>
              {!collapsed && (
                <ChevronDown
                  className={`ml-auto w-4 h-4 shrink-0 text-secondary-500 transition-transform motion-reduce:transition-none ${moreOpen ? 'rotate-180' : ''}`}
                  aria-hidden
                />
              )}
            </button>

            {moreOpen && (
              <div id="nav-more" className="mt-2">
                {more.map(({ group, items }, i) => (
                  <div
                    key={group.id}
                    role="group"
                    aria-labelledby={`nav-group-${group.id}`}
                    className={i === 0 ? '' : collapsed ? 'mt-3 border-t border-secondary-100 pt-3' : 'mt-4'}
                  >
                    <p
                      id={`nav-group-${group.id}`}
                      className={collapsed ? 'sr-only' : 'px-3 pb-2 text-[0.6875rem] font-semibold uppercase tracking-wider text-secondary-500'}
                    >
                      {group.caption}
                    </p>
                    <div className="space-y-1">{items.map(menuLink)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </nav>

      {/* Footer */}
      <div className="px-3 py-3 border-t border-secondary-100 space-y-1">
        {menuLink(GUIDE_ITEM)}
        {/* Not a page of the menu map: it is a standalone form, so it is a plain link, marked like the others when open. */}
        <NavLink
          to="/change-password"
          title={collapsed ? undefined : 'Choose a new password'}
          onClick={() => {
            setTip(null);
            onNavigate?.();
          }}
          {...railTip('Change password', 'Choose a new password.')}
          className="group relative w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap text-secondary-600 hover:bg-secondary-50 hover:text-secondary-900 transition-colors motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
        >
          <KeyRound className="w-5 h-5 shrink-0 text-secondary-400 group-hover:text-secondary-600 transition-colors" />
          <span className={collapsed ? 'sr-only' : ''}>Change password</span>
        </NavLink>
        <button
          onClick={logout}
          {...railTip('Logout')}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap text-secondary-600 hover:bg-error-50 hover:text-error-600 transition-colors motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
        >
          <LogOut className="w-5 h-5 shrink-0" />
          <span className={collapsed ? 'sr-only' : ''}>Logout</span>
        </button>
      </div>

      {tip &&
        createPortal(
          <div
            role="tooltip"
            style={{ top: tip.top }}
            className="fixed left-[4.75rem] z-[60] w-max max-w-[16rem] -translate-y-1/2 rounded-xl bg-white px-3 py-2 text-left shadow-lg ring-1 ring-gray-200 contrast-more:ring-gray-500"
          >
            <p className="text-[0.8125rem] font-semibold text-gray-900">{tip.label}</p>
            {tip.purpose && <p className="mt-0.5 text-xs leading-snug text-gray-600">{tip.purpose}</p>}
          </div>,
          document.body
        )}
    </div>
  );
}
